import { useEffect, useRef, useState } from "react";
import { Sparkles, Anchor, Calendar, MapPin } from "lucide-react";

/**
 * Hero AI chat demo — static scripted typewriter loop.
 * Sequence:
 *   1. user bubble types in
 *   2. typing dots (~800ms)
 *   3. assistant bubble types in (with chips/cards)
 *   4. small pause, then fade-reset → loop
 */
const USER_TEXT = "I need a pontoon Saturday afternoon, seats 8.";
const ASSISTANT_TEXT =
  "Found 2 available at Lakeside Marina, 1–5pm — $340 for 4hrs. Want me to book one?";

export default function AIChat() {
  const [userTyped, setUserTyped] = useState("");
  const [thinking, setThinking] = useState(false);
  const [assistantTyped, setAssistantTyped] = useState("");
  const [showCards, setShowCards] = useState(false);
  const stage = useRef("idle");

  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    const typeInto = async (text, setter, ms = 28) => {
      for (let i = 1; i <= text.length; i++) {
        if (cancelled) return;
        setter(text.slice(0, i));
        await wait(ms);
      }
    };

    const run = async () => {
      while (!cancelled) {
        // reset
        stage.current = "user";
        setUserTyped("");
        setAssistantTyped("");
        setShowCards(false);
        setThinking(false);
        await wait(400);

        await typeInto(USER_TEXT, setUserTyped, 28);
        if (cancelled) return;
        await wait(500);

        stage.current = "thinking";
        setThinking(true);
        await wait(900);
        setThinking(false);
        if (cancelled) return;

        stage.current = "assistant";
        await typeInto(ASSISTANT_TEXT, setAssistantTyped, 22);
        if (cancelled) return;

        await wait(400);
        setShowCards(true);

        await wait(5200);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="relative w-full max-w-[440px] rounded-3xl border border-ra-line bg-white p-5 shadow-card"
      aria-label="AI booking assistant demo"
      data-testid="ai-chat-demo"
    >
      {/* header */}
      <div className="flex items-center justify-between border-b border-ra-line pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ra-ocean/10">
            <Sparkles size={14} className="text-ra-ocean" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-ra-ink leading-tight">Booking Assistant</p>
            <p className="text-[11px] text-ra-ink/55 leading-tight">Lakeside Marina · Online</p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
        </span>
      </div>

      {/* user bubble */}
      <div className="mt-4 flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-ra-ocean px-4 py-2.5 text-sm text-white shadow-soft">
          {userTyped || "\u00A0"}
          {stage.current === "user" && userTyped.length < USER_TEXT.length && (
            <span className="ml-0.5 inline-block h-3 w-[2px] bg-white align-middle animate-blink" />
          )}
        </div>
      </div>

      {/* thinking */}
      {thinking && (
        <div className="mt-3 flex">
          <div className="rounded-2xl rounded-bl-md bg-ra-paper px-4 py-2.5">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-ra-ink/40 animate-bounce [animation-delay:-0.2s]" />
              <span className="h-1.5 w-1.5 rounded-full bg-ra-ink/40 animate-bounce [animation-delay:-0.1s]" />
              <span className="h-1.5 w-1.5 rounded-full bg-ra-ink/40 animate-bounce" />
            </span>
          </div>
        </div>
      )}

      {/* assistant bubble */}
      {assistantTyped && (
        <div className="mt-3 flex">
          <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-ra-paper px-4 py-2.5 text-sm text-ra-ink">
            {assistantTyped}
            {stage.current === "assistant" && assistantTyped.length < ASSISTANT_TEXT.length && (
              <span className="ml-0.5 inline-block h-3 w-[2px] bg-ra-ink/70 align-middle animate-blink" />
            )}
          </div>
        </div>
      )}

      {/* result cards */}
      <div
        className={`mt-3 grid grid-cols-1 gap-2 transition-all duration-500 ${
          showCards ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-2"
        }`}
        aria-hidden={!showCards}
      >
        {[
          { name: "Bennington 23 SSX", seats: 10, price: "$340", slot: "1–5pm" },
          { name: "Sun Tracker Party Cruiser", seats: 8, price: "$295", slot: "2–6pm" },
        ].map((b) => (
          <button
            key={b.name}
            type="button"
            className="group flex items-center justify-between rounded-xl border border-ra-line bg-white px-3 py-2.5 text-left transition-all hover:border-ra-ocean hover:shadow-soft"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-ra-ocean/10 text-ra-ocean">
                <Anchor size={16} />
              </div>
              <div className="leading-tight">
                <p className="text-[13px] font-semibold text-ra-ink">{b.name}</p>
                <p className="flex items-center gap-2 text-[11px] text-ra-ink/60">
                  <span className="inline-flex items-center gap-1"><Calendar size={10} />{b.slot}</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={10} />Seats {b.seats}</span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[13px] font-semibold text-ra-ink">{b.price}</p>
              <p className="text-[10px] uppercase tracking-wider text-ra-ocean opacity-0 transition-opacity group-hover:opacity-100">Book →</p>
            </div>
          </button>
        ))}
      </div>

      {/* footer hint */}
      <div className="mt-4 flex items-center justify-between border-t border-ra-line pt-3 text-[11px] text-ra-ink/50">
        <span>Powered by RentAnything.ai</span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-ra-amber" />
          Connected to live inventory
        </span>
      </div>

      {/* floating accent decorations */}
      <div className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 rounded-full bg-ra-amber/20 blur-2xl" />
      <div className="pointer-events-none absolute -left-4 -bottom-4 h-20 w-20 rounded-full bg-ra-ocean/20 blur-2xl" />
    </div>
  );
}
